
import React from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Eye, Trash2, BarChart3 } from "lucide-react";

const QuestionBankCard = ({
  question,
  index,
  users,
  uniqueUsers,
  onView,
  onDelete,
  convertISOtoLocale,
  hideDelete = false,
}) => {
  // Calculate statistics
  const currentCount = question.questionCount || 0;
  const maxCapacity = question.maxCapacity || 5000;
  const percentage = Math.round((currentCount / maxCapacity) * 100);
  const isAtCapacity = currentCount >= maxCapacity;

  // Get creator and modifier names
  const creatorName = uniqueUsers
    .filter((user) => user._id === question.createdBy)
    .map((user) => user.name)[0] || "Unknown";

  const modifierName = question.lastModifiedBy
    ? uniqueUsers.find((u) => u._id === question.lastModifiedBy)?.name || "Unknown"
    : "-----";

  return (
    <Card
      key={index}
      className="border rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 bg-white"
    >
      <CardHeader className="font-bold text-md border rounded-t-2xl">
        <div className="flex justify-between items-start">
          <span>{question.name}</span>
          <div className="flex items-center space-x-1 text-xs text-gray-600">
            <BarChart3 className="w-3 h-3" />
            <span>{percentage}%</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="flex flex-col">
          <div className="flex justify-between items-center border-b pb-2 mb-2">
            <span className="text-gray-600 font-medium">Total Questions:</span>
            <span
              className={`px-3 py-1 rounded-full text-sm font-semibold ${
                isAtCapacity ? "bg-red-100 text-red-800" : "bg-gray-100"
              }`}
            >
              {currentCount}
            </span>
          </div>

          <div className="flex justify-between items-center border-b pb-2 mb-2">
            <span className="text-gray-600 font-medium">Capacity:</span>
            <span className="text-sm font-semibold">
              {currentCount}/{maxCapacity}
            </span>
          </div>

          <div className="mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-gray-600 text-xs">Usage</span>
              <span className="text-gray-600 text-xs">{percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  percentage >= 90
                    ? "bg-red-500"
                    : percentage >= 70
                    ? "bg-yellow-500"
                    : "bg-green-500"
                }`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              ></div>
            </div>
          </div>

          <div className="flex justify-between items-center mb-2 mt-2">
            <div className="flex flex-col">
              <span className="text-gray-600 text-sm font-medium">Created By</span>
              <span className="text-gray-900 font-semibold">{creatorName}</span>
            </div>
            <div className="flex flex-col text-right">
              <span className="text-gray-600 text-sm font-medium">Created At</span>
              <span className="text-gray-900 font-semibold">
                {convertISOtoLocale(question.createdAt)}
              </span>
            </div>
          </div>

          <div className="flex justify-between items-center mb-2">
            <div className="flex flex-col">
              <span className="text-gray-600 text-sm font-medium">Last Modified By</span>
              <span className="text-gray-900 font-semibold">{modifierName}</span>
            </div>
            <div className="flex flex-col text-right">
              <span className="text-gray-600 text-sm font-medium">Modified At</span>
              <span className="text-gray-900 font-semibold">
                {question.lastModifiedAt
                  ? convertISOtoLocale(question.lastModifiedAt)
                  : convertISOtoLocale(question.updatedAt)}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="justify-end mt-5">
        <div className="flex space-x-2 w-full">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onView(question)}
            className="flex items-center space-x-1 bg-gray-100 hover:bg-gray-200 w-1/2 rounded"
          >
            <Eye className="w-[16px]" />
            <span>View</span>
          </Button>
          {!hideDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(question._id, question.name)}
              className="flex items-center space-x-1 bg-red-100 hover:bg-red-200 text-red-700 w-1/2 rounded"
            >
              <Trash2 className="h-4 w-[16px]" />
              <span>Delete</span>
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

export default QuestionBankCard;